import { PropsRecomendacaoPublicacao } from '@/components/molecules/RecomendacaoPublicacao';
import { PaginaPublicacaoProps } from '@/components/templates/PaginaPublicacaoTemplate';
import { useScrollAteTopo } from '@/hooks/useScrollAteTopo';
import { useSnackbar } from '@/hooks/useSnackbar';
import {
  recuperarNoticiaPorSlug,
  recuperarNoticiasRelacionadas,
} from '@/services/conteudoService';
import { Noticia } from '@/types/noticia';
import converterDataParaFormatoISO from '@/utils/converterDataParaFormatoISO';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

function useNoticiasPaginaHook(props: PaginaPublicacaoProps) {
  const { $publicacaoSlug } = props;
  const caminhoURL = usePathname();
  const [noticia, setNoticia] = useState<Noticia>();
  const [noticiasRecomendadas, setNoticiasRecomendadas] =
    useState<PropsRecomendacaoPublicacao[]>();
  const [noticiaSendoCarregada, setNoticiaSendoCarregada] =
    useState<boolean>(false);

  const { mostrarSnackbar } = useSnackbar();

  const { scrollarAteOTopo } = useScrollAteTopo();

  async function recuperarNoticia() {
    try {
      setNoticiaSendoCarregada(true);
      const retornoNoticiaFirebase =
        await recuperarNoticiaPorSlug($publicacaoSlug);
      if (!retornoNoticiaFirebase)
        throw new Error('Não foi possível recuperar a noticia');

      const retornoNoticiasRecomendadas = await recuperarNoticiasRelacionadas({
        noticiaSlug: retornoNoticiaFirebase.slug,
        tagsNoticia: retornoNoticiaFirebase.tags,
      });
      setNoticiasRecomendadas(retornoNoticiasRecomendadas);
      setNoticia(retornoNoticiaFirebase);
      scrollarAteOTopo();
    } catch (error: unknown) {
      if (error instanceof Error) {
        mostrarSnackbar({
          color: 'error',
          highContrast: true,
          mensagem: error.message ?? '',
          size: 1,
          variant: 'solid',
        });
      }
    } finally {
      setNoticiaSendoCarregada(false);
    }
  }

  useEffect(() => {
    recuperarNoticia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jsonLDPaginaNoticia = useMemo(() => {
    if (!noticia) return null;

    const { titulo, imagemArquivo, dataNoticia, publicadoPor } = noticia;
    const dataISO = converterDataParaFormatoISO(dataNoticia);
    return {
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      image: [imagemArquivo.urlx1 || '/assets/images/logoCirculo@3x.png'],
      headline: titulo,
      datePublished: dataISO,
      dateModified: dataISO,
      author: [{ name: publicadoPor, '@type': 'Person' }],
    };
  }, [noticia]);

  return {
    noticia,
    noticiaSendoCarregada,
    caminhoURL,
    jsonLDPaginaNoticia,
    noticiasRecomendadas,
  };
}

export type useNoticiasPaginaHookTypeReturn = ReturnType<
  typeof useNoticiasPaginaHook
>;

export default useNoticiasPaginaHook;
